import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import '../styles/admin.css';

const AdminDepartments = () => {
  const { user: currentUser } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');


  const fetchDepartments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/departments');
      setDepartments(res.data.data || []);
    } catch {
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  if (currentUser?.role !== 'admin') {
    return <div className="loading">Access denied</div>;
  }

  if (loading) return <div className="loading">Loading...</div>;

  const startEdit = (dept) => {
    setEditingId(dept.id);
    setEditName(dept.name);
  };

  const saveEdit = async (dept) => {
    if (!editName.trim()) return;
    try {
      await api.put(`/departments/${dept.id}`, { name: editName.trim() });
      setDepartments(prev => prev.map(d => d.id === dept.id ? { ...d, name: editName.trim() } : d));
      setEditingId(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    }
  };

  const toggleStatus = async (dept) => {
    const newStatus = dept.status === 'active' ? 'inactive' : 'active';
    setDepartments(prev => prev.map(d => d.id === dept.id ? { ...d, status: newStatus } : d));
    try {
      await api.put(`/departments/${dept.id}`, { status: newStatus });
    } catch (err) {
      toast.error('Failed to update status');
      fetchDepartments();
    }
  };

  const addDepartment = async () => {
    if (!newName.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      await api.post('/departments', { name: newName.trim() });
      toast.success('Department created');
      setNewName('');
      setShowAdd(false);
      fetchDepartments();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create');
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Departments</h1>
        <div className="header-controls">
          <span className="user-count">{departments.length} departments</span>
          <button className="add-user-btn" onClick={() => setShowAdd(true)}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Department
          </button>
        </div>
      </div>

      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Employees</th>
              <th>Status</th>
              <th style={{ width: '120px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {departments.map(dept => (
              <tr key={dept.id}>
                <td>
                  {editingId === dept.id ? (
                    <input
                      type="text"
                      className="inline-edit-input"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit(dept);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      autoFocus
                    />
                  ) : (
                    <strong>{dept.name}</strong>
                  )}
                </td>
                <td>{dept.active_employee_count || 0}</td>
                <td>
                  <span
                    className={`status-dot ${dept.status === 'active' ? 'active' : 'inactive'}`}
                    onClick={() => toggleStatus(dept)}
                    style={{ cursor: 'pointer' }}
                  >
                    {dept.status || 'active'}
                  </span>
                </td>
                <td>
                  {editingId === dept.id ? (
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button className="btn-save-sm" onClick={() => saveEdit(dept)}>Save</button>
                      <button className="btn-cancel-sm" onClick={() => setEditingId(null)}>Cancel</button>
                    </div>
                  ) : (
                    <button className="edit-user-btn" onClick={() => startEdit(dept)} title="Edit name">
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Department Modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal-content add-user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Department</h2>
              <button className="modal-close" onClick={() => setShowAdd(false)}>&times;</button>
            </div>
            <div className="add-user-form">
              <div className="form-field">
                <label>Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Department name"
                  autoFocus
                />
              </div>
              <div className="form-actions">
                <button className="btn-cancel" onClick={() => setShowAdd(false)}>Cancel</button>
                <button className="btn-save" onClick={addDepartment}>Create</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDepartments;
